import { getQualifyingColumnType, ID, Lot } from 'types';
import DatadogService from '@services/DatadogService';
import { SettleResult, watchSettle } from '@providers/LineItemsProvider/utils/settleWatcher';
import type { ListedLot } from '@features/buyer-line-items-pagination';

const REFRESH_SETTLE_MS = 30_000;
const HEAP_SETTLE_MS = 5_000;
const SETTLE_QUIET_MS = 2_000;
const EDIT_STAMP_MAX_AGE_MS = 5_000;
const UNSAMPLED_EDIT_VITALS = 100;
const EDIT_VITAL_SAMPLE_EVERY = 10;
const HEAP_BUCKET_MB = 50;
const BYTES_PER_MB = 1024 * 1024;

type SettleOutcome = 'capped' | 'empty' | 'hidden' | 'refreshed' | 'settled' | 'unsupported';
type MarkName = 'first_lot_painted' | 'grid_dom_built' | 'grid_drawn' | 'grid_usable' | 'load_started' | 'lots_fetched';

interface Stamp {
  epoch: number;
  perf: number;
}

interface EventContext {
  custom_columns: number;
  heap_mb?: number;
  heap_mb_settled?: number;
  id: ID;
  line_item_questions: number;
  line_items: number;
  lots: number;
  pagination_enabled: boolean;
}

interface PerfState {
  awaitingData: boolean;
  describedViews: Set<string>;
  editVitalCounts: Map<string, number>;
  eventContext?: EventContext;
  eventId?: ID;
  expectedLots: Set<ID>;
  firstDirtyEdit?: Stamp;
  firstLotPainted: boolean;
  loadStarted?: Stamp;
  loadToken: number;
  lotIndex: Map<ID, number>;
  paintedLots: Map<ID, Stamp>;
  pendingEdits: Map<ID, Stamp>;
  pendingFirstRepaint?: Stamp;
  previewActive: boolean;
  refreshTimer?: ReturnType<typeof setTimeout>;
  reportedLots: Set<ID>;
  saveStart?: Stamp;
  settledViews: Set<string>;
  stopSettleWatch?: () => void;
  stopVisibilityWatch?: () => void;
  timedSettlePending: boolean;
  viewId?: string;
  wasHidden: boolean;
}

const createState = (): PerfState => ({
  awaitingData: true,
  describedViews: new Set(),
  editVitalCounts: new Map(),
  expectedLots: new Set(),
  firstLotPainted: false,
  loadToken: 0,
  lotIndex: new Map(),
  paintedLots: new Map(),
  pendingEdits: new Map(),
  previewActive: false,
  reportedLots: new Set(),
  settledViews: new Set(),
  timedSettlePending: false,
  wasHidden: false,
});

let state = createState();
const recordedMarks = new Map<MarkName, number>();
let afterChangeDepth = 0;

const now = (): Stamp => ({ epoch: Date.now(), perf: performance.now() });

/**
 * One wall-clock offset per load, so gaps between timings match the perf clock exactly. Wall clock, not
 * performance.timeOrigin: RUM measures from a Date.now() view start, and timeOrigin drifts in long-lived tabs.
 */
const epochOf = (perf: number): number => {
  const anchor = state.loadStarted ?? now();
  return anchor.epoch - anchor.perf + perf;
};

const safely = (fn: () => void): void => {
  try {
    fn();
  } catch (error) {
    DatadogService.captureDebugMessage?.('lineItemsPerf failed', { error: String(error) });
  }
};

const mark = (name: MarkName, perf: number): void => {
  recordedMarks.set(name, perf);
  if (typeof performance.mark !== 'function') return;
  performance.clearMarks?.(name);
  performance.mark(name, { detail: { token: state.loadToken }, startTime: perf });
};

const afterPaint = (fn: () => void): void => {
  const run = () => setTimeout(() => safely(fn), 0);
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else run();
};

const viewKey = (): string => `${state.viewId ?? 'none'}:${state.eventId}`;

const isSameView = (): boolean => DatadogService.getViewId?.() === state.viewId;

const heapMb = (): number | undefined => {
  const { memory } = (performance as Performance & { memory?: { usedJSHeapSize: number } });
  if (!memory) return undefined;
  return Math.round(memory.usedJSHeapSize / BYTES_PER_MB / HEAP_BUCKET_MB) * HEAP_BUCKET_MB;
};

const writeEventContext = (patch: Partial<EventContext>): void => {
  if (!state.eventContext || !isSameView()) return;
  state.eventContext = { ...state.eventContext, ...patch };
  DatadogService.setViewContextProperty?.('event', state.eventContext);
};

const writeSettleOutcome = (outcome: SettleOutcome): void => {
  if (isSameView()) DatadogService.setViewContextProperty?.('grid_settle', outcome);
};

const stopSettleTracking = (): void => {
  state.stopSettleWatch?.();
  state.stopSettleWatch = undefined;
  state.stopVisibilityWatch?.();
  state.stopVisibilityWatch = undefined;
  state.timedSettlePending = false;
};

const clearLoadTracking = (): void => {
  if (state.timedSettlePending) writeSettleOutcome('refreshed');
  stopSettleTracking();
  state.expectedLots.clear();
  state.reportedLots.clear();
  state.paintedLots.clear();
  state.firstLotPainted = false;
  if (state.refreshTimer) clearTimeout(state.refreshTimer);
  state.refreshTimer = undefined;
};

const sampleWeight = (name: string): number | undefined => {
  const count = (state.editVitalCounts.get(name) ?? 0) + 1;
  state.editVitalCounts.set(name, count);
  if (count <= UNSAMPLED_EDIT_VITALS) return 1;
  return count % EDIT_VITAL_SAMPLE_EVERY === 0 ? EDIT_VITAL_SAMPLE_EVERY : undefined;
};

const sendVital = (name: string, start: Stamp, endPerf: number, context: Record<string, unknown> = {}): void => {
  if (!isSameView()) return;
  DatadogService.addDurationVital?.(name, {
    context,
    duration: endPerf - start.perf,
    startTime: start.epoch,
  });
};

const sendEditVital = (name: string, start: Stamp, endPerf: number, context: Record<string, unknown> = {}): void => {
  const weight = sampleWeight(name);
  if (weight === undefined) return;
  sendVital(name, start, endPerf, weight === 1 ? context : { ...context, sample_weight: weight });
};

const isFresh = (stamp: Stamp | undefined, perf: number): stamp is Stamp =>
  !!stamp && perf - stamp.perf <= EDIT_STAMP_MAX_AGE_MS;

/** Claims the first load of this view and event for RUM and writes its heap size; false for any later load. */
const claimTimedLoad = (): boolean => {
  const key = viewKey();
  if (state.settledViews.has(key) || !isSameView()) return false;
  state.settledViews.add(key);
  const token = state.loadToken;
  writeEventContext({ heap_mb: heapMb() });
  setTimeout(() => safely(() => {
    if (token === state.loadToken) writeEventContext({ heap_mb_settled: heapMb() });
  }), HEAP_SETTLE_MS);
  return true;
};

const onSettleDone = (result: SettleResult, token: number, timed: boolean): void => {
  if (token !== state.loadToken) return;
  const { wasHidden } = state;
  stopSettleTracking();
  if (result.outcome === 'settled') mark('grid_usable', result.perf);
  if (!timed) return;
  if (wasHidden) {
    writeSettleOutcome('hidden');
    return;
  }
  if (result.outcome === 'settled' && isSameView()) DatadogService.addTiming?.('grid_usable', epochOf(result.perf));
  writeSettleOutcome(result.outcome);
};

const finishDrawn = (): void => {
  const stamps = [...state.paintedLots.values()].map(stamp => stamp.perf);
  const perf = stamps.length ? Math.max(...stamps) : performance.now();
  const token = state.loadToken;
  state.awaitingData = true;
  mark('grid_drawn', perf);

  const timed = claimTimedLoad();
  if (timed && !state.wasHidden) {
    if (state.loadStarted) DatadogService.addTiming?.('load_started', epochOf(state.loadStarted.perf));
    DatadogService.addTiming?.('grid_drawn', epochOf(perf));
  }
  const stopWatch = watchSettle(perf, SETTLE_QUIET_MS, result => safely(() => onSettleDone(result, token, timed)));
  if (!stopWatch) {
    stopSettleTracking();
    if (timed) writeSettleOutcome(state.wasHidden ? 'hidden' : 'unsupported');
    return;
  }
  state.stopSettleWatch = stopWatch;
  state.timedSettlePending = timed;
};

const finishEmpty = (): void => {
  state.awaitingData = true;
  stopSettleTracking();
  if (claimTimedLoad()) writeSettleOutcome('empty');
};

const watchVisibility = (): void => {
  state.wasHidden = document.visibilityState === 'hidden';
  const onChange = () => {
    if (document.visibilityState === 'hidden') state.wasHidden = true;
  };
  document.addEventListener('visibilitychange', onChange);
  state.stopVisibilityWatch = () => document.removeEventListener('visibilitychange', onChange);
};

const consumeEdits = (lotId: ID, perf: number): void => {
  if (isFresh(state.pendingFirstRepaint, perf)) {
    sendEditVital('li_edit_to_first_repaint', state.pendingFirstRepaint, perf);
  }
  state.pendingFirstRepaint = undefined;

  const edit = state.pendingEdits.get(lotId);
  if (!edit) return;
  state.pendingEdits.delete(lotId);
  if (isFresh(edit, perf)) {
    sendEditVital('li_edit_to_state_repaint', edit, perf, { lot_index: state.lotIndex.get(lotId) });
  }
};

export const beginLoad = (eventId: ID): void => safely(() => {
  clearLoadTracking();
  state.loadToken += 1;
  state.awaitingData = true;
  state.eventId = eventId;
  state.viewId = DatadogService.getViewId?.();
  state.pendingEdits.clear();
  state.pendingFirstRepaint = undefined;
  if (state.previewActive) state.settledViews.add(viewKey());
  watchVisibility();
  state.loadStarted = now();
  mark('load_started', state.loadStarted.perf);

  if (state.settledViews.has(viewKey())) {
    const token = state.loadToken;
    state.refreshTimer = setTimeout(() => safely(() => {
      if (token === state.loadToken) clearLoadTracking();
    }), REFRESH_SETTLE_MS);
  }
});

/** Read when a fetch starts, so a slower, older fetch cannot report into a newer load. */
export const getLoadToken = (): number => state.loadToken;

const recordLots = <T extends ListedLot>(
  lots: T[],
  token: number,
  rowCount: (lot: T) => number,
  paginationEnabled: boolean,
): void => safely(() => {
  if (token !== state.loadToken || !state.awaitingData || state.eventId === undefined) return;
  const perf = performance.now();
  mark('lots_fetched', perf);

  const qualifying = getQualifyingColumnType();
  state.lotIndex = new Map(lots.map((lot, index) => [lot.id, index]));
  state.expectedLots = new Set(lots.filter(lot => rowCount(lot) > 0).map(lot => lot.id));
  state.eventContext = {
    custom_columns: lots.reduce((sum, lot) =>
      sum + (lot.custom_column_headers ?? []).filter(header => header.column_type !== qualifying).length, 0),
    id: state.eventId,
    line_item_questions: lots.reduce((sum, lot) => sum + (lot.line_item_questions_count ?? 0), 0),
    line_items: lots.reduce((sum, lot) => sum + rowCount(lot), 0),
    lots: lots.length,
    pagination_enabled: paginationEnabled,
  };
  state.awaitingData = false;

  const key = viewKey();
  const isFirstForView = !state.describedViews.has(key);
  if (isFirstForView) {
    state.describedViews.add(key);
    writeEventContext({});
  }
  if (isFirstForView && !state.settledViews.has(key) && isSameView()) {
    DatadogService.addTiming?.('lots_fetched', epochOf(perf));
  }
  if (state.expectedLots.size === 0) finishEmpty();
});

export const lotsFetched = (lots: Lot[], token: number): void =>
  recordLots(lots, token, lot => lot.line_items?.length ?? 0, false);

export const lotsListed = (lots: ListedLot[], token: number): void =>
  recordLots(lots, token, lot => lot.line_items_count ?? 0, true);

/**
 * Call when a lot's grid has rendered but not painted; the stamp is taken after paint. Unforced renders are
 * ignored, and a render forced from inside afterChange is never taken as an edit's repaint.
 */
export const lotPainted = (lotId: ID, isForced: boolean): void => safely(() => {
  if (!isForced) return;
  const token = state.loadToken;
  const tracking = !state.awaitingData && state.expectedLots.has(lotId) && !state.reportedLots.has(lotId);
  const hasEdit = afterChangeDepth === 0 && (!!state.pendingFirstRepaint || state.pendingEdits.has(lotId));
  if (!tracking && !hasEdit) return;

  if (tracking) {
    state.reportedLots.add(lotId);
    if (state.reportedLots.size === state.expectedLots.size) mark('grid_dom_built', performance.now());
  }

  afterPaint(() => {
    const painted = now();
    if (hasEdit) consumeEdits(lotId, painted.perf);
    if (!tracking || token !== state.loadToken || state.awaitingData) return;

    state.paintedLots.set(lotId, painted);
    if (!state.firstLotPainted) {
      state.firstLotPainted = true;
      mark('first_lot_painted', painted.perf);
      if (!state.settledViews.has(viewKey()) && isSameView()) {
        DatadogService.addTiming?.('first_lot_painted', epochOf(painted.perf));
      }
    }
    if (state.paintedLots.size === state.expectedLots.size) finishDrawn();
  });
});

/** Counts depth, not a flag: clearing cells inside afterChange fires a nested afterChange. */
export const withinAfterChange = (fn: () => void): void => {
  afterChangeDepth += 1;
  try {
    fn();
  } finally {
    afterChangeDepth -= 1;
  }
};

export const editCommitted = (lotId: ID): void => safely(() => {
  if (afterChangeDepth > 1) return;
  const stamp = now();
  state.pendingEdits.set(lotId, stamp);
  state.pendingFirstRepaint = stamp;
  state.firstDirtyEdit ??= stamp;
});

const saveStarted = (): void => safely(() => {
  state.saveStart = now();
});

const saveFinished = (ok: boolean): void => safely(() => {
  const start = state.saveStart;
  state.saveStart = undefined;
  if (!start) return;
  const end = performance.now();
  sendVital('li_save_roundtrip', start, end, { ok });
  if (ok && state.firstDirtyEdit) {
    sendVital('li_edit_to_save', state.firstDirtyEdit, end);
    state.firstDirtyEdit = undefined;
  }
});

export const timeSave = async <T extends { success?: boolean }>(save: () => Promise<T>): Promise<T> => {
  saveStarted();
  try {
    const response = await save();
    saveFinished(!!response.success);
    return response;
  } catch (error) {
    saveFinished(false);
    throw error;
  }
};

/** Call alongside every DISCARD_CHANGES dispatch, or li_edit_to_save measures from a discarded edit. */
export const discardEdits = (): void => safely(() => {
  state.firstDirtyEdit = undefined;
  state.pendingEdits.clear();
  state.pendingFirstRepaint = undefined;
});

/** While the preview is active, the current view/event pair and any load that starts are never timed. */
export const setPreviewActive = (active: boolean): void => safely(() => {
  state.previewActive = active;
  if (!active) return;
  if (state.eventId !== undefined) state.settledViews.add(viewKey());
  state.loadToken += 1;
  state.awaitingData = true;
  state.eventId = undefined;
  clearLoadTracking();
});

/** Clears everything except the described and settled view/event pairs and the preview flag. */
export const reset = (): void => safely(() => {
  clearLoadTracking();
  const { describedViews, loadToken, previewActive, settledViews } = state;
  state = { ...createState(), describedViews, loadToken: loadToken + 1, previewActive, settledViews };
});

export const getMarks = (): Partial<Record<MarkName, number>> => Object.fromEntries(recordedMarks);

export const resetForTests = (): void => {
  stopSettleTracking();
  clearLoadTracking();
  state = createState();
  recordedMarks.clear();
  afterChangeDepth = 0;
};
